import { formatMoney } from '@theme/money-formatting';

const PRODUCT_SELECT_EVENT = 'shopify:product:select';

class BundleAndSave extends HTMLElement {
  connectedCallback() {
    if (this.initialized) return;

    this.initialized = true;
    this.options = Array.from(this.querySelectorAll('[data-bundle-option]:not([hidden])'));
    this.section = this.closest('.shopify-section');
    this.form = this.closest('form');
    this.quantityInput = this.form?.querySelector('input[name="quantity"]');
    this.variantIdInput = this.form?.querySelector('input[name="id"]');
    this.basePrice = Number(this.dataset.basePrice || 0);
    this.currency = this.dataset.currency || 'USD';
    this.moneyFormat = this.dataset.moneyFormat || '${{amount}}';
    this.mode = this.dataset.bundleMode || 'quantity';
    this.selectedQuantity = Number(this.options.find((option) => option.classList.contains('is-selected'))?.dataset.quantity || 1);
    this.variantPicker = this.findBundleVariantPicker();
    if (this.mode === 'variant') {
      this.options.forEach((option) => {
        if (!option.dataset.variantId) {
          option.hidden = true;
        }
      });
      this.options = this.options.filter((option) => !option.hidden);
    }

    this.options.forEach((option) => {
      option.addEventListener('click', () => this.selectOption(option));
    });

    this.quantityInput?.addEventListener('input', this.syncFromQuantity);
    this.quantityInput?.addEventListener('change', this.syncFromQuantity);
    this.section?.addEventListener(PRODUCT_SELECT_EVENT, this.handleProductSelect);
    this.form?.addEventListener('submit', this.syncBeforeSubmit, true);
    this.form?.addEventListener('click', this.handleFormClick, true);

    this.syncVariantId(this.dataset.defaultVariantId);
    requestAnimationFrame(() => this.syncVariantId(this.getSelectedVariantId()));
    setTimeout(() => this.syncVariantId(this.getSelectedVariantId()), 250);
    this.bootstrapTimer = window.setInterval(() => {
      const input = this.closest('form')?.querySelector('input[name="id"]');
      if (input?.value) {
        window.clearInterval(this.bootstrapTimer);
        return;
      }
      this.syncVariantId(this.getSelectedVariantId());
    }, 300);
    this.updateAvailability();
    this.render();
  }

  disconnectedCallback() {
    if (this.bootstrapTimer) window.clearInterval(this.bootstrapTimer);
    this.quantityInput?.removeEventListener('input', this.syncFromQuantity);
    this.quantityInput?.removeEventListener('change', this.syncFromQuantity);
    this.section?.removeEventListener(PRODUCT_SELECT_EVENT, this.handleProductSelect);
    this.form?.removeEventListener('submit', this.syncBeforeSubmit, true);
    this.form?.removeEventListener('click', this.handleFormClick, true);
  }

  selectOption(option) {
    if (option.disabled) return;

    this.options.forEach((item) => {
      const selected = item === option;
      item.classList.toggle('is-selected', selected);
      item.setAttribute('aria-pressed', String(selected));
    });

    this.selectedQuantity = Number(option.dataset.quantity || 1);
    if (this.mode === 'variant') {
      this.selectBundleVariant(option);
    } else {
      this.setQuantity(this.selectedQuantity);
    }
    this.render();
  }

  findBundleVariantPicker() {
    const pickers = Array.from(this.section?.querySelectorAll('variant-picker') || []);

    for (const picker of pickers) {
      const fieldset = Array.from(picker.querySelectorAll('fieldset')).find((candidate) =>
        candidate.querySelector('legend')?.textContent?.trim().toLowerCase().includes('buy more save more')
      );

      if (fieldset) {
        fieldset.classList.add('bundle-and-save__source-picker');
        return { picker, fieldset };
      }
    }

    return null;
  }

  selectBundleVariant(option) {
    const variantId = option.dataset.variantId;
    if (!variantId || !this.variantPicker) return;

    this.syncVariantId(variantId);
    const variantInput = this.variantPicker.fieldset.querySelector(`[data-variant-id="${variantId}"]`);
    if (variantInput && !variantInput.checked) {
      variantInput.click();
    }
  }

  syncVariantId(variantId) {
    const currentForm = this.closest('form');
    const currentVariantIdInput = currentForm?.querySelector('input[name="id"]');

    if (variantId && currentVariantIdInput) {
      this.variantIdInput = currentVariantIdInput;
      currentVariantIdInput.value = String(variantId);
    }
  }

  getSelectedVariantId() {
    const selectedOption = this.options.find((option) => option.classList.contains('is-selected'));
    return selectedOption?.dataset.variantId || this.dataset.defaultVariantId || '';
  }

  setQuantity(quantity) {
    if (!this.quantityInput) return;

    const min = Number(this.quantityInput.min || this.dataset.minQuantity || 1);
    const max = this.quantityInput.max ? Number(this.quantityInput.max) : Infinity;
    const nextQuantity = Math.min(max, Math.max(min, quantity));

    this.quantityInput.value = String(nextQuantity);
    this.quantityInput.dispatchEvent(new Event('input', { bubbles: true }));
    this.quantityInput.dispatchEvent(new Event('change', { bubbles: true }));
  }

  syncFromQuantity = () => {
    const quantity = Number(this.quantityInput?.value || 1);
    const matchingOption = this.options.find((option) => Number(option.dataset.quantity) === quantity);

    if (matchingOption && !matchingOption.disabled) {
      this.options.forEach((item) => {
        const selected = item === matchingOption;
        item.classList.toggle('is-selected', selected);
        item.setAttribute('aria-pressed', String(selected));
      });
      this.selectedQuantity = quantity;
    } else {
      this.options.forEach((item) => {
        item.classList.remove('is-selected');
        item.setAttribute('aria-pressed', 'false');
      });
      this.selectedQuantity = quantity;
    }

    this.render();
  };

  syncBeforeSubmit = () => {
    this.syncVariantId(this.getSelectedVariantId());
  };

  handleFormClick = (event) => {
    if (event.target instanceof Element && event.target.closest('button[name="add"], [type="submit"]')) {
      this.syncBeforeSubmit();
    }
  };

  updateAvailability() {
    const min = Number(this.quantityInput?.min || this.dataset.minQuantity || 1);
    const max = this.quantityInput?.max ? Number(this.quantityInput.max) : Infinity;

    this.options.forEach((option) => {
      const quantity = Number(option.dataset.quantity || 1);
      option.disabled = quantity < min || quantity > max;
    });

    const current = this.options.find(
      (option) => Number(option.dataset.quantity) === this.selectedQuantity && !option.disabled
    );
    const firstAvailable = this.options.find((option) => !option.disabled);

    if (!current && firstAvailable) {
      this.selectOption(firstAvailable);
    }
  }

  handleProductSelect = (event) => {
    if (event.target instanceof Element && event.target.closest('.shopify-section') !== this.section) return;
    if (!event.promise || typeof event.promise.then !== 'function') return;

    event.promise
      .then(({ variant }) => {
        if (!variant?.price?.amount) return;
        if (this.mode === 'variant') {
          this.variantPicker = this.findBundleVariantPicker() || this.variantPicker;
        }
        this.currency = variant.price.currencyCode || this.currency;
        if (this.mode === 'variant') {
          const selectedOption = this.options.find((option) => option.dataset.variantId === String(variant.id));
          if (selectedOption) {
            this.options.forEach((option) => {
              const selected = option === selectedOption;
              option.classList.toggle('is-selected', selected);
              option.setAttribute('aria-pressed', String(selected));
            });
            this.selectedQuantity = Number(selectedOption.dataset.quantity || 1);
          }
          this.syncVariantId(variant.id);
        } else {
          this.basePrice = this.majorToMinor(variant.price.amount, this.currency);
          this.dataset.basePrice = String(this.basePrice);
          this.syncVariantId(variant.id);
        }
        this.render();
      })
      .catch((error) => {
        if (error?.name !== 'AbortError') console.warn('[bundle-and-save] Variant update failed:', error);
      });
  };

  majorToMinor(amount, currency) {
    const precision = new Intl.NumberFormat(undefined, {
      style: 'currency',
      currency,
    }).resolvedOptions().maximumFractionDigits;
    return Math.round(Number(amount) * 10 ** precision);
  }

  format(minorAmount) {
    try {
      return formatMoney(Math.max(0, Math.round(minorAmount)), this.moneyFormat, this.currency);
    } catch {
      const precision = new Intl.NumberFormat(undefined, {
        style: 'currency',
        currency: this.currency,
      }).resolvedOptions().maximumFractionDigits;
      return new Intl.NumberFormat(undefined, {
        style: 'currency',
        currency: this.currency,
        minimumFractionDigits: precision,
        maximumFractionDigits: precision,
      }).format(minorAmount / 10 ** precision);
    }
  }

  render() {
    const unitPrice = Number(this.basePrice || 0);

    this.options.forEach((option) => {
      const quantity = Number(option.dataset.quantity || 1);
      const discount = Number(option.dataset.discount || 0);
      const subtotal = unitPrice * quantity;
      const variantPrice = Number(option.dataset.variantPrice || 0);
      const total =
        this.mode === 'variant' && variantPrice > 0
          ? variantPrice
          : Math.round((subtotal * (100 - discount)) / 100);
      const savings = Math.max(0, subtotal - total);
      const priceElement = option.querySelector('[data-bundle-price]');
      const unitElement = option.querySelector('[data-bundle-unit]');
      const saveElement = option.querySelector('[data-bundle-save]');
      const sublineElement = option.querySelector('[data-bundle-subline]');
      const actualDiscount = subtotal > 0 ? Math.round((savings / subtotal) * 100) : 0;

      if (priceElement) priceElement.textContent = this.format(total);
      if (unitElement) unitElement.textContent = `${this.format(Math.round(total / quantity))} each`;
      if (saveElement) saveElement.textContent = savings ? `Save ${this.format(savings)}` : '';
      if (sublineElement && this.mode === 'variant') {
        sublineElement.textContent = savings ? `Save ${actualDiscount}%` : 'Standard price';
      }
    });
  }
}

if (!customElements.get('bundle-and-save')) {
  customElements.define('bundle-and-save', BundleAndSave);
}
